/**
 * Financial statements — `/finance/statements`.
 *
 * The profit and loss account, the balance sheet, and the indirect cash-flow
 * statement, on one screen because they are read together and share every
 * control: a period, a layout, a currency slice, and a comparative.
 *
 * Four facts the screen states rather than smooths over.
 *
 * `Period` is the selected fiscal period alone and `year to date` is period 1
 * through it — the trial balance's semantics, unchanged. On the balance sheet
 * the year-to-date column IS the closing balance, so the columns are labelled
 * for what they are rather than "this month" and "total".
 *
 * A statement is either computed now or read from the record filed when the
 * period closed, and the screen says which. That is not a caching detail: an
 * open period's figures may still legitimately change; a closed period's must
 * not.
 *
 * The assertions that ran are shown with both sides, because a proof a reader
 * cannot redo is just a claim. They are always true in a response — a
 * violation is a 500, never a report with a caveat.
 *
 * A layout that maps an account nowhere is a 422 listing every hole, and the
 * screen renders that list instead of "something went wrong", because that is
 * the one error the reader can actually act on.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { MoneyDisplay } from "@/components/money/money-display";
import { formatCalendarDate, formatDateTime } from "@/lib/format";
import { listFiscalYears, type FiscalYear } from "@/features/ledger/calendar-api";

import { blockedLayout, getFinancialStatement, type StatementType } from "../statements-api";
import { listStatementLayouts } from "../statement-layouts-api";

const STATEMENTS: readonly StatementType[] = ["profit_and_loss", "balance_sheet", "cash_flow"];

export interface StatementsPageProps {
  baseCurrency?: string;
}

export function StatementsPage({ baseCurrency = "" }: StatementsPageProps) {
  const { t, i18n } = useTranslation();
  const [statementType, setStatementType] = useState<StatementType>("profit_and_loss");
  const [periodId, setPeriodId] = useState("");
  const [layoutId, setLayoutId] = useState("");
  const [comparative, setComparative] = useState<"prior_year" | "none">("prior_year");

  const years = useQuery<FiscalYear[]>({
    queryKey: ["ledger/fiscal-years"],
    queryFn: ({ signal }) => listFiscalYears(signal),
  });
  const layouts = useQuery({
    queryKey: ["finance/statement-layouts"],
    queryFn: async ({ signal }) => [...(await listStatementLayouts({}, signal)).items],
  });

  const periods = useMemo(
    () =>
      (years.data ?? []).flatMap((year) =>
        year.periods.map((period) => ({ id: period.id, label: `${year.code} · ${period.name}` })),
      ),
    [years.data],
  );
  const layoutChoices = useMemo(
    () => (layouts.data ?? []).filter((layout) => layout.statement_type === statementType),
    [layouts.data, statementType],
  );

  const statement = useQuery({
    queryKey: ["finance/statement", statementType, periodId, layoutId, comparative],
    queryFn: ({ signal }) =>
      getFinancialStatement(
        statementType,
        {
          fiscal_period_id: periodId,
          comparative,
          ...(layoutId === "" ? {} : { layout_id: layoutId }),
        },
        signal,
      ),
    enabled: periodId !== "",
    retry: false,
  });

  const day = (iso: string): string => {
    try {
      return formatCalendarDate(iso, i18n.language);
    } catch {
      return iso;
    }
  };
  const stamp = (iso: string): string => {
    try {
      return formatDateTime(iso, i18n.language);
    } catch {
      return iso;
    }
  };

  const blocked = blockedLayout(statement.error);

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="space-y-1">
        <h1 className="text-xl font-semibold tracking-tight">
          {t("finance.statements.title", "Financial statements")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(
            "finance.statements.subtitle",
            "Grouped by the layout that was in force at the period end, so an earlier period still reads the way it did.",
          )}
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="statement-type">{t("finance.statements.statement", "Statement")}</Label>
          <Select
            value={statementType}
            onValueChange={(value) => {
              setStatementType(value as StatementType);
              setLayoutId("");
            }}
          >
            <SelectTrigger id="statement-type" className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATEMENTS.map((type) => (
                <SelectItem key={type} value={type}>
                  {t(`finance.statementType.${type}`, type)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="statement-period">{t("finance.statements.period", "Period")}</Label>
          <Select value={periodId} onValueChange={setPeriodId}>
            <SelectTrigger id="statement-period" className="w-64">
              <SelectValue placeholder={t("finance.statements.pickPeriod", "Pick a period…")} />
            </SelectTrigger>
            <SelectContent>
              {periods.map((period) => (
                <SelectItem key={period.id} value={period.id}>
                  {period.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="statement-layout">{t("finance.statements.layout", "Layout")}</Label>
          <Select
            value={layoutId === "" ? "__inforce__" : layoutId}
            onValueChange={(value) => setLayoutId(value === "__inforce__" ? "" : value)}
          >
            <SelectTrigger id="statement-layout" className="w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__inforce__">
                {t("finance.statements.inForce", "Whatever was in force")}
              </SelectItem>
              {layoutChoices.map((layout) => (
                <SelectItem key={layout.id} value={layout.id}>
                  {layout.name} · v{layout.version}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="statement-comparative">
            {t("finance.statements.comparative", "Compare with")}
          </Label>
          <Select
            value={comparative}
            onValueChange={(value) => setComparative(value as "prior_year" | "none")}
          >
            <SelectTrigger id="statement-comparative" className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="prior_year">
                {t("finance.statements.priorYear", "The year before")}
              </SelectItem>
              <SelectItem value="none">
                {t("finance.statements.noComparative", "Nothing")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {periodId === "" ? (
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">
              {t("finance.statements.pickFirst", "Pick a period to read a statement.")}
            </p>
          </CardContent>
        </Card>
      ) : statement.isLoading ? (
        <p className="text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
      ) : blocked !== null ? (
        <Card>
          <CardHeader>
            <CardTitle>
              <h2>{t("finance.statements.blockedTitle", "This layout cannot produce it yet")}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {t(
                "finance.statements.blockedHint",
                "Every account must land on exactly one line. Until then the statement would silently omit a balance, so it is not produced at all.",
              )}
            </p>
            <p className="text-sm">
              {t(
                "finance.statementLayouts.counts",
                "{{mapped}} of {{eligible}} accounts mapped · {{unmapped}} unmapped · {{untagged}} untagged",
                {
                  mapped: blocked.mapped_accounts,
                  eligible: blocked.eligible_accounts,
                  unmapped: blocked.unmapped_accounts,
                  untagged: blocked.untagged_accounts,
                },
              )}
            </p>
            <ul className="space-y-1 text-sm">
              {blocked.issues.map((issue, index) => (
                <li
                  key={`${issue.code}-${issue.account_id ?? index}`}
                  className="rounded-md border p-2"
                >
                  <span className="block font-medium">
                    {t(`finance.statementValidation.${issue.code}`, issue.code.replace(/_/g, " "))}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {issue.account_code === undefined ? null : (
                      <span dir="ltr" className="font-mono">
                        {issue.account_code}{" "}
                      </span>
                    )}
                    {issue.detail}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : statement.data === undefined ? (
        <p role="alert" className="text-sm text-destructive">
          {t("finance.statements.error", "The statement could not be read.")}
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge variant={statement.data.source === "snapshot" ? "secondary" : "outline"}>
              {statement.data.source === "snapshot"
                ? t("finance.statements.fromSnapshot", "As filed at close")
                : t("finance.statements.live", "Computed now")}
            </Badge>
            <span className="text-muted-foreground">
              {statement.data.layout.name} · v{statement.data.layout.version} ·{" "}
              {day(statement.data.fiscal_period.ends_on)}
            </span>
            <span className="text-xs text-muted-foreground" dir="ltr">
              {statement.data.presentation_currency === ""
                ? baseCurrency
                : statement.data.presentation_currency}
            </span>
          </div>

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[46rem] text-sm">
                  <thead>
                    <tr className="border-b text-xs uppercase text-muted-foreground">
                      <th scope="col" className="p-2 text-start">
                        {t("finance.statements.line", "Line")}
                      </th>
                      <th scope="col" className="p-2 text-end">
                        {t("finance.statements.periodColumn", "This period")}
                      </th>
                      <th scope="col" className="p-2 text-end">
                        {statement.data.statement_type === "balance_sheet"
                          ? t("finance.statements.closing", "Closing balance")
                          : t("finance.statements.yearToDate", "Year to date")}
                      </th>
                      {comparative === "none" ? null : (
                        <>
                          <th scope="col" className="p-2 text-end">
                            {t("finance.statements.priorPeriod", "Same period before")}
                          </th>
                          <th scope="col" className="p-2 text-end">
                            {t("finance.statements.priorYtd", "Before, to date")}
                          </th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {statement.data.lines.map((line) => (
                      <tr
                        key={line.line_id}
                        className={`border-b last:border-0 ${
                          line.kind === "subtotal" ? "font-medium" : ""
                        }`}
                      >
                        <td className="p-2">
                          <span
                            className="block"
                            style={{ paddingInlineStart: `${line.depth * 1.25}rem` }}
                          >
                            {line.label}
                          </span>
                          {line.drill_down.accounts.length === 0 ? null : (
                            <span
                              className="block text-xs text-muted-foreground"
                              style={{ paddingInlineStart: `${line.depth * 1.25}rem` }}
                            >
                              {t("finance.statements.accountCount", "{{count}} account(s)", {
                                count: line.drill_down.accounts.length,
                              })}
                            </span>
                          )}
                        </td>
                        <td className="p-2 text-end">
                          <MoneyDisplay value={line.amounts.period} display="none" />
                        </td>
                        <td className="p-2 text-end">
                          <MoneyDisplay value={line.amounts.year_to_date} display="none" />
                        </td>
                        {comparative === "none" ? null : (
                          <>
                            <td className="p-2 text-end text-muted-foreground">
                              {line.amounts.comparative_period === null ? (
                                "—"
                              ) : (
                                <MoneyDisplay
                                  value={line.amounts.comparative_period}
                                  display="none"
                                />
                              )}
                            </td>
                            <td className="p-2 text-end text-muted-foreground">
                              {line.amounts.comparative_year_to_date === null ? (
                                "—"
                              ) : (
                                <MoneyDisplay
                                  value={line.amounts.comparative_year_to_date}
                                  display="none"
                                />
                              )}
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>
                <h2>{t("finance.statements.assertionsTitle", "What was proved")}</h2>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-sm text-muted-foreground">
                {t(
                  "finance.statements.assertionsHint",
                  "Both sides are published so the subtraction can be redone. A statement whose proof fails is not returned at all.",
                )}
              </p>
              <ul className="space-y-1 text-sm">
                {statement.data.assertions.map((assertion) => (
                  <li
                    key={assertion.code}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-2"
                  >
                    <span>{t(`finance.assertion.${assertion.code}`, assertion.code)}</span>
                    <span className="flex items-center gap-3 tabular-nums" dir="ltr">
                      <MoneyDisplay value={assertion.left} display="none" />
                      <span aria-hidden="true">=</span>
                      <MoneyDisplay value={assertion.right} display="none" />
                    </span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground">
                {t("finance.statements.generatedAt", "Produced {{time}}", {
                  time: stamp(statement.data.generated_at),
                })}
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
